import { firestore } from '@src/api/Firebase/Firebase';
import { collection } from 'firebase/firestore';
import { ExpenseGroup } from '@api/CashFlows/CashFlow';

// ============================================================================
// GASTOS FIJOS — `recurring-expenses` (plan de Gastos §3.4, F4).
//
// Una plantilla por gasto que se repite cada mes (renta, internet, luz). Registrar
// el primero la crea junto con la instancia de ese mes; **los meses siguientes los
// genera el aseo diario del server** (5:00 a.m.) con id determinista
// `{plantilla}_{yyyy-MM}` en `cash-flows` y `create()` (si ya existe, no la pisa).
//
// - Monto fijo: la instancia nace con el monto de la plantilla y ya cuenta.
// - Monto variable (o que sale de la caja): nace **por confirmar** con el último
//   monto; el Estado de Resultados la cuenta como "estimado" y no resta en el corte
//   hasta que el admin de la sucursal escribe el monto del recibo.
//
// Pausar o eliminar una plantilla no toca los meses ya generados. Lecturas: la
// pestaña "Gastos fijos" lee las plantillas de las sucursales visibles (decenas).
// ============================================================================

export type RecurringAmountMode = 'fixed' | 'variable';
export type RecurringStatus = 'active' | 'paused' | 'deleted';

export interface RecurringExpense {
    id: string;
    branchOfficeId: string;
    categoryId: string;
    categoryName: string;
    categoryGroup: ExpenseGroup;
    description: string;
    /** El último confirmado: con él nace la instancia del mes siguiente. */
    amount: number;
    amountMode: RecurringAmountMode;
    /** ¿Sale del efectivo de la caja? Casi siempre no (transferencia). */
    paidFromCash: boolean;
    /** `yyyy-MM` del primer mes. */
    startPeriod: string;
    /** `yyyy-MM` de la instancia más nueva que existe. */
    lastPeriod: string;
    status: RecurringStatus;
    createdBy: string;
    createdByName?: string;
    createdAt: any;
    updatedAt?: any;
}

export const RECURRING_EXPENSES_COLLECTION = 'recurring-expenses';
export const recurringExpensesRef = collection(firestore, RECURRING_EXPENSES_COLLECTION);

/** El id de la instancia de un mes: el mismo que escribe el aseo del server. */
export const recurringInstanceId = (recurringExpenseId: string, period: string): string => `${recurringExpenseId}_${period}`;

export const RECURRING_STATUS_LABELS: Record<RecurringStatus, string> = {
    active: 'Activo',
    paused: 'Pausado',
    deleted: 'Eliminado'
};

export const AMOUNT_MODE_LABELS: Record<RecurringAmountMode, string> = {
    fixed: 'Mismo monto cada mes',
    variable: 'Monto variable (se confirma)'
};
